import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["searchInput", "typeFilter", "statusFilter", "priorityFilter", "assigneeFilter", "item", "noResults", "resultCount"]
  
  connect() {
    this.applyFilters()
  }
  
  search() {
    this.applyFilters()
  }
  
  filterByType() {
    this.applyFilters()
  }
  
  filterByStatus() {
    this.applyFilters()
  }
  
  filterByPriority() {
    this.applyFilters()
  }
  
  filterByAssignee() {
    this.applyFilters()
  }
  
  clearFilters() {
    // Reset all filters
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ''
    if (this.hasTypeFilterTarget) this.typeFilterTarget.value = ''
    if (this.hasStatusFilterTarget) this.statusFilterTarget.value = ''
    if (this.hasPriorityFilterTarget) this.priorityFilterTarget.value = ''
    if (this.hasAssigneeFilterTarget) this.assigneeFilterTarget.value = ''
    
    this.applyFilters()
  }
  
  applyFilters() {
    const searchTerm = this.hasSearchInputTarget ? this.searchInputTarget.value.toLowerCase() : ''
    const typeFilter = this.hasTypeFilterTarget ? this.typeFilterTarget.value : ''
    const statusFilter = this.hasStatusFilterTarget ? this.statusFilterTarget.value : ''
    const priorityFilter = this.hasPriorityFilterTarget ? this.priorityFilterTarget.value : ''
    const assigneeFilter = this.hasAssigneeFilterTarget ? this.assigneeFilterTarget.value : ''
    
    let visibleCount = 0
    let hiddenCount = 0
    
    this.itemTargets.forEach(item => {
      const title = item.dataset.itemTitle?.toLowerCase() || ''
      const description = item.dataset.itemDescription?.toLowerCase() || ''
      const type = item.dataset.itemType || ''
      const status = item.dataset.itemStatus || ''
      const priority = item.dataset.itemPriority || ''
      const assignee = item.dataset.itemAssignee || ''
      
      // Check all filters
      const matchesSearch = !searchTerm || 
                          title.includes(searchTerm) || 
                          description.includes(searchTerm) ||
                          assignee.toLowerCase().includes(searchTerm)
      const matchesType = !typeFilter || type === typeFilter
      const matchesStatus = !statusFilter || status === statusFilter
      const matchesPriority = !priorityFilter || priority === priorityFilter
      const matchesAssignee = !assigneeFilter || assignee === assigneeFilter
      
      if (matchesSearch && matchesType && matchesStatus && matchesPriority && matchesAssignee) {
        item.style.display = ''
        item.classList.remove('hidden')
        visibleCount++
      } else {
        item.style.display = 'none'
        item.classList.add('hidden')
        hiddenCount++
      }
    })
    
    // Update result count
    if (this.hasResultCountTarget) {
      this.resultCountTarget.textContent = `Showing ${visibleCount} of ${visibleCount + hiddenCount} items`
    }
    
    // Show/hide no results message
    if (this.hasNoResultsTarget) {
      if (visibleCount === 0 && this.itemTargets.length > 0) {
        this.noResultsTarget.classList.remove('hidden')
      } else {
        this.noResultsTarget.classList.add('hidden')
      }
    }
    
    // Update active filter indicators
    this.updateFilterIndicators()
  }
  
  updateFilterIndicators() {
    const hasActiveFilters = 
      (this.hasSearchInputTarget && this.searchInputTarget.value) ||
      (this.hasTypeFilterTarget && this.typeFilterTarget.value) ||
      (this.hasStatusFilterTarget && this.statusFilterTarget.value) ||
      (this.hasPriorityFilterTarget && this.priorityFilterTarget.value) ||
      (this.hasAssigneeFilterTarget && this.assigneeFilterTarget.value)
    
    // Show/hide clear filters button
    const clearButton = this.element.querySelector('[data-action*="clearFilters"]')
    if (clearButton) {
      if (hasActiveFilters) {
        clearButton.classList.remove('hidden')
      } else {
        clearButton.classList.add('hidden')
      }
    }
    
    // Add visual indicators to active filters
    this.updateFilterSelect(this.typeFilterTarget)
    this.updateFilterSelect(this.statusFilterTarget)
    this.updateFilterSelect(this.priorityFilterTarget)
    this.updateFilterSelect(this.assigneeFilterTarget)
  }
  
  updateFilterSelect(selectElement) {
    if (!selectElement) return
    
    if (selectElement.value) {
      selectElement.classList.add('select-primary')
    } else {
      selectElement.classList.remove('select-primary')
    }
  }
  
  // Advanced search with debouncing
  debounceSearch() {
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => {
      this.search()
    }, 300)
  }
  
  // Sort functionality
  sortBy(event) {
    const sortField = event.currentTarget.dataset.sortField
    const sortDirection = event.currentTarget.dataset.sortDirection || 'asc'
    
    // Convert NodeList to Array for sorting
    const items = Array.from(this.itemTargets)
    
    items.sort((a, b) => {
      let aValue = a.dataset[`item${this.capitalize(sortField)}`] || ''
      let bValue = b.dataset[`item${this.capitalize(sortField)}`] || ''
      
      // Handle numeric values
      if (sortField === 'priority') {
        aValue = parseInt(aValue) || 0
        bValue = parseInt(bValue) || 0
      }
      
      // Handle dates
      if (sortField === 'dueDate' || sortField === 'createdAt') {
        aValue = new Date(aValue || 0)
        bValue = new Date(bValue || 0)
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1
      } else {
        return aValue < bValue ? 1 : -1
      }
    })
    
    // Reorder DOM elements
    const container = items[0].parentElement
    items.forEach(item => container.appendChild(item))
    
    // Toggle sort direction for next click
    event.currentTarget.dataset.sortDirection = sortDirection === 'asc' ? 'desc' : 'asc'
    
    // Update sort indicators
    this.updateSortIndicators(event.currentTarget)
  }
  
  updateSortIndicators(activeButton) {
    // Remove active state from all sort buttons
    this.element.querySelectorAll('[data-sort-field]').forEach(button => {
      button.classList.remove('btn-active')
      const icon = button.querySelector('.sort-icon')
      if (icon) {
        icon.classList.remove('rotate-180')
      }
    })
    
    // Add active state to current button
    activeButton.classList.add('btn-active')
    const icon = activeButton.querySelector('.sort-icon')
    if (icon && activeButton.dataset.sortDirection === 'desc') {
      icon.classList.add('rotate-180')
    }
  }
  
  capitalize(string) {
    return string.charAt(0).toUpperCase() + string.slice(1)
  }
  
  // Bulk selection support
  toggleAll(event) {
    const checked = event.target.checked
    this.itemTargets.forEach(item => {
      const checkbox = item.querySelector('input[type="checkbox"]')
      if (checkbox && !item.classList.contains('hidden')) {
        checkbox.checked = checked
        this.updateItemSelection(item, checked)
      }
    })
    this.updateBulkActions()
  }
  
  toggleItem(event) {
    const item = event.target.closest('[data-action-items-filter-target="item"]')
    this.updateItemSelection(item, event.target.checked)
    this.updateBulkActions()
  }
  
  updateItemSelection(item, selected) {
    if (selected) {
      item.classList.add('bg-base-200')
    } else {
      item.classList.remove('bg-base-200')
    }
  }
  
  updateBulkActions() {
    const selectedCount = this.itemTargets.filter(item => {
      const checkbox = item.querySelector('input[type="checkbox"]')
      return checkbox && checkbox.checked
    }).length
    
    const bulkActions = this.element.querySelector('[data-bulk-actions]')
    if (bulkActions) {
      if (selectedCount > 0) {
        bulkActions.classList.remove('hidden')
        const countElement = bulkActions.querySelector('[data-selected-count]')
        if (countElement) {
          countElement.textContent = `${selectedCount} selected`
        }
      } else {
        bulkActions.classList.add('hidden')
      }
    }
  }
  
  getSelectedIds() {
    return this.itemTargets
      .filter(item => {
        const checkbox = item.querySelector('input[type="checkbox"]')
        return checkbox && checkbox.checked
      })
      .map(item => item.dataset.itemId)
  }
}